import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { T, Icon, StatusPill } from "../businessTheme";
import { useEffect } from "react";
import { supabase } from "../../lib/supabase";

/* ── Reserved Pro card ── */
function TeamProCard({ pro, onRemove, canManage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div style={{ padding: "20px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{
            width: 44, height: 44, borderRadius: 13, background: pro.colorSet.bg,
            border: `1.5px solid ${pro.colorSet.border}`, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700, color: pro.colorSet.text, flexShrink: 0,
          }}>
            {pro.initials}
          </div>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: T.n900 }}>{pro.name}</div>
            <div style={{ fontSize: 12, color: T.n400 }}>{pro.role}</div>
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: T.n400 }}>
            <Icon name="ChevronDown" size={16} strokeWidth={2} />
          </button>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: T.white, border: `1px solid ${T.n150}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 50, minWidth: 160, overflow: "hidden" }}>
                <button
                  onClick={() => { navigate(`/pro/${pro.id}`); setMenuOpen(false); }}
                  style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: T.n700, fontFamily: T.sans }}>
                  View profile
                </button>
                <button
                  onClick={() => { navigate(`/messages/conv_${pro.id}`); setMenuOpen(false); }}
                  style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: T.n700, fontFamily: T.sans }}>
                  Message
                </button>
                {/* Remove — disabled for non-Owner/Admin roles */}
                <button
                  disabled={!canManage}
                  onClick={() => { if (canManage) { onRemove(pro.id); setMenuOpen(false); } }}
                  style={{
                    width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none",
                    cursor: canManage ? "pointer" : "not-allowed",
                    fontSize: 12.5, color: canManage ? T.red : T.n300, fontFamily: T.sans,
                    opacity: canManage ? 1 : 0.5,
                  }}>
                  Remove from team
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <StatusPill status={pro.status} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, paddingTop: 12, borderTop: `1px solid ${T.n100}` }}>
        <div>
          <div style={{ fontSize: 11, color: T.n400, marginBottom: 2 }}>Rating</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600, color: T.n800 }}>
            <Icon name="Star" size={12} color={T.amber} strokeWidth={0} style={{ fill: T.amber }} />
            {pro.rating}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: T.n400, marginBottom: 2 }}>Jobs this month</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.n800 }}>{pro.jobsThisMonth}</div>
        </div>
      </div>

      <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 9, background: T.n50, fontSize: 11.5, color: T.n500 }}>
        {pro.commitment} · Reserved since {pro.reservedSince}
      </div>
    </div>
  );
}

/* ── Reserve-a-Pro modal (kept for fallback; primary flow is now Search Results Page) ── */
function ReserveProModal({ open, onClose, onConfirm, slotsRemaining, alreadyReserved, pool, isPriorityPool }) {
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1);
  const [commitment, setCommitment] = useState("standard");

  if (!open) return null;

  const reservedIds = new Set(alreadyReserved.map(p => p.id));

  const COMMITMENTS = [
    { id: "light",    label: "Light",          sub: "1-2 days/week priority access" },
    { id: "standard", label: "Standard",        sub: "3-4 days/week, primary business" },
    { id: "full",     label: "Full exclusive",  sub: "Fully dedicated to your business" },
  ];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 100%)", maxHeight: "85vh", background: T.white, borderRadius: 20, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "22px 26px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: 0 }}>
              Reserve a Pro
              {isPriorityPool && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, color: T.purple, background: T.purpleBg, border: `1px solid ${T.purpleBorder}`, borderRadius: 99, padding: "3px 10px", verticalAlign: "middle" }}>
                  Business Priority
                </span>
              )}
            </h3>
            <p style={{ fontSize: 12.5, color: T.n400, margin: "3px 0 0" }}>
              {slotsRemaining === null ? "Negotiated slots on your plan" : `${slotsRemaining} slot${slotsRemaining !== 1 ? "s" : ""} remaining on your plan`}
            </p>
          </div>
          <button onClick={onClose} style={{ background: T.n50, border: `1px solid ${T.n150}`, borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="X" size={14} color={T.n600} strokeWidth={2.2} />
          </button>
        </div>

        <div style={{ padding: "22px 26px", overflowY: "auto", flex: 1 }}>
          {step === 1 && (
            <>
              <p style={{ fontSize: 13, color: T.n500, margin: "0 0 16px" }}>
                {isPriorityPool
                  ? "Choose from the curated Business Priority pool — the highest-rated Pros on the platform."
                  : "Choose a Pro from the curated list available in your area."}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pool.map(p => {
                  const taken = reservedIds.has(p.id);
                  const isSelected = selected?.id === p.id;
                  return (
                    <button key={p.id} disabled={taken}
                      onClick={() => setSelected(p)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                        borderRadius: 12, border: `1.5px solid ${isSelected ? T.g500 : T.n150}`,
                        background: isSelected ? T.g50 : taken ? T.n50 : T.white,
                        cursor: taken ? "not-allowed" : "pointer", textAlign: "left", fontFamily: T.sans,
                        opacity: taken ? 0.5 : 1, width: "100%",
                      }}>
                      <div style={{ width: 38, height: 38, borderRadius: 11, background: p.colorSet.bg, border: `1.5px solid ${p.colorSet.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, color: p.colorSet.text, flexShrink: 0 }}>
                        {p.initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: T.n400 }}>{p.role} · {p.city}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12.5, fontWeight: 600, color: T.n700 }}>
                          <Icon name="Star" size={11} color={T.amber} strokeWidth={0} style={{ fill: T.amber }} />
                          {p.rating}
                        </div>
                        <div style={{ fontSize: 11, color: T.n400 }}>{p.jobs} jobs</div>
                      </div>
                      {taken && <span style={{ fontSize: 10.5, fontWeight: 700, color: T.n400 }}>ON TEAM</span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 2 && selected && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px", borderRadius: 12, background: T.g50, border: `1px solid ${T.g100}`, marginBottom: 20 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: selected.colorSet.bg, border: `1.5px solid ${selected.colorSet.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, color: selected.colorSet.text }}>
                  {selected.initials}
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: T.n500 }}>{selected.role}</div>
                </div>
              </div>

              <p style={{ fontSize: 13, fontWeight: 600, color: T.n800, margin: "0 0 12px" }}>Set their commitment level</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {COMMITMENTS.map(c => (
                  <button key={c.id} onClick={() => setCommitment(c.id)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px",
                    borderRadius: 12, border: `1.5px solid ${commitment === c.id ? T.g500 : T.n150}`,
                    background: commitment === c.id ? T.g50 : T.white, cursor: "pointer", textAlign: "left", fontFamily: T.sans, width: "100%",
                  }}>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{c.label}</div>
                      <div style={{ fontSize: 12, color: T.n400 }}>{c.sub}</div>
                    </div>
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%", border: `2px solid ${commitment === c.id ? T.g500 : T.n200}`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {commitment === c.id && <div style={{ width: 9, height: 9, borderRadius: "50%", background: T.g500 }} />}
                    </div>
                  </button>
                ))}
              </div>

              <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", gap: 9 }}>
                <Icon name="Info" size={14} color={T.amber} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 12, color: "#92400e", margin: 0, lineHeight: 1.6 }}>
                  This reservation uses one slot included in your subscription. There is no extra charge for reserving this Pro.
                </p>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: "16px 26px", borderTop: `1px solid ${T.n100}`, display: "flex", justifyContent: "space-between", gap: 10 }}>
          <button onClick={() => step === 1 ? onClose() : setStep(1)} style={{ padding: "11px 20px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step === 1 ? (
            <button disabled={!selected} onClick={() => setStep(2)} style={{
              padding: "11px 24px", borderRadius: 10, border: "none",
              background: selected ? T.g500 : T.n200, color: T.white, fontSize: 13, fontWeight: 600,
              fontFamily: T.sans, cursor: selected ? "pointer" : "not-allowed",
            }}>
              Continue
            </button>
          ) : (
            <button onClick={() => { onConfirm(selected, commitment); setStep(1); setSelected(null); }} style={{
              padding: "11px 24px", borderRadius: 10, border: "none", background: T.g500, color: T.white,
              fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
            }}>
              Confirm reservation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ */

export default function TeamSection({ account, plan, userRole, onNavigate }) {
  const navigate = useNavigate();
  const [reserved, setReserved] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [pool, setPool] = useState([]);

  useEffect(() => {
    if (!account?.id) return;
    (async () => {
      const [{ data: reservedRows }, { data: jobs }] = await Promise.all([
        supabase
          .from("business_reserved_pros")
          .select(`
            id, status, reserved_at, commitment, pro_id,
            pro_profiles!pro_id ( id, avg_rating, profiles!user_id ( full_name ) )
          `)
          .eq("business_id", account.id)
          .eq("status", "active"),
        supabase
          .from("job_requests")
          .select("pro_id, status, created_at")
          .eq("business_id", account.id),
      ]);

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const activeStatuses = ["submitted", "matched", "confirmed"];

      const jobsThisMonthByPro = {};
      const currentlyOnJobByPro = new Set();
      (jobs || []).forEach(j => {
        if (new Date(j.created_at) >= startOfMonth) {
          jobsThisMonthByPro[j.pro_id] = (jobsThisMonthByPro[j.pro_id] || 0) + 1;
        }
        if (j.status === "confirmed") currentlyOnJobByPro.add(j.pro_id);
      });

      setReserved((reservedRows || []).map(r => {
        const name = r.pro_profiles?.profiles?.full_name || "Pro";
        const initials = name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
        return {
          id: r.pro_id,
          name,
          role: "Reserved Pro",
          initials,
          colorSet: { bg: "#EEF2FF", border: "#C7D2FE", text: "#4338CA" },
          status: currentlyOnJobByPro.has(r.pro_id) ? "on_job" : "available",
          rating: r.pro_profiles?.avg_rating || 0,
          jobsThisMonth: jobsThisMonthByPro[r.pro_id] || 0,
          reservedSince: new Date(r.reserved_at).toLocaleDateString("en-KE", { month: "short", year: "numeric" }),
          commitment: r.commitment || "Not set",
        };
      }));
    })();
  }, [account?.id]);

  // Real pool of approved, active pros to browse and reserve directly —
  // makes the fallback modal below genuinely functional, not dead code.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("pro_profiles")
        .select("id, avg_rating, completed_jobs, category, profiles!user_id ( full_name, location )")
        .eq("account_status", "active");

      setPool((data || []).map(p => {
        const name = p.profiles?.full_name || "Pro";
        return {
          id: p.id,
          name,
          role: p.category || "Professional",
          city: p.profiles?.location || "",
          initials: name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase(),
          colorSet: { bg: "#EEF2FF", border: "#C7D2FE", text: "#4338CA" },
          rating: p.avg_rating || 0,
          jobs: p.completed_jobs || 0,
        };
      }));
    })();
  }, []);

  const isPriorityPool  = plan.proPool === "priority";

  const unlimited       = plan.teamSlots === Infinity || plan.teamSlots === null;
  const slotsUsed       = reserved.length;
  const slotsTotal      = unlimited ? null : plan.teamSlots;
  const slotsRemaining  = unlimited ? null : Math.max(0, slotsTotal - slotsUsed);
  const atCapacity      = !unlimited && slotsUsed >= slotsTotal;
  const pct             = unlimited ? 0 : Math.min(100, Math.round((slotsUsed / slotsTotal) * 100));

  // Only Owner and Admin can remove pros
  const canManage = userRole === "Owner" || userRole === "Admin";

  const handleRemove = async (id) => {
    const prevReserved = reserved;
    setReserved(r => r.filter(p => p.id !== id));
    const { error } = await supabase
      .from("business_reserved_pros")
      .update({ status: "removed" })
      .eq("business_id", account.id)
      .eq("pro_id", id);
    if (error) {
      console.error("Failed to remove reserved pro:", error);
      setReserved(prevReserved);
    }
  };

  const handleConfirm = async (pro, commitment) => {
    if (atCapacity) return;
    const { error } = await supabase
      .from("business_reserved_pros")
      .insert({ business_id: account.id, pro_id: pro.id, status: "active", reserved_at: new Date().toISOString(), commitment });
    if (error) {
      console.error("Failed to reserve pro:", error);
      return;
    }
    setReserved(r => [...r, {
      id: pro.id, name: pro.name, role: pro.role, initials: pro.initials,
      colorSet: pro.colorSet, status: "available",
      reservedSince: new Date().toLocaleDateString("en-KE", { month: "short", year: "numeric" }),
      rating: pro.rating, jobsThisMonth: 0, commitment,
    }]);
    setModalOpen(false);
  };

  return (
    <div>
      {/* ── Slot usage banner ── */}
      <div style={{
        padding: "22px 24px", borderRadius: 16,
        background: `linear-gradient(135deg, ${T.g600}, ${T.g500})`,
        color: T.white, marginBottom: 22,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Icon name="Layers" size={15} color="#fff" strokeWidth={1.8} />
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.85 }}>
              {plan.label} plan
            </span>
          </div>
          <div style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 400, marginBottom: 8 }}>
            {slotsUsed} of {unlimited ? "Unlimited" : slotsTotal} Pros reserved
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: unlimited ? 0 : 12 }}>
            Included in your {plan.price ? `KES ${plan.price.toLocaleString()}/month` : "custom"} subscription — no extra team fee.
          </div>

          {/* Progress bar — hidden for unlimited plans */}
          {!unlimited && (
            <div style={{ maxWidth: 280 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 11.5, opacity: 0.8 }}>Slots used</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, opacity: atCapacity ? 1 : 0.9 }}>
                  {slotsUsed} / {slotsTotal}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,.25)", overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 99, transition: "width .3s",
                  width: `${pct}%`,
                  background: atCapacity ? "#FCA5A5" : "#fff",
                }} />
              </div>
            </div>
          )}
        </div>

        {/* Slot dot indicators — only for small slot counts */}
        {!unlimited && slotsTotal <= 10 && (
          <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
            {Array.from({ length: slotsTotal }).map((_, i) => (
              <div key={i} style={{
                width: 10, height: 10, borderRadius: "50%",
                background: i < slotsUsed ? "#fff" : "rgba(255,255,255,.3)",
              }} />
            ))}
          </div>
        )}
      </div>

      {/* ── At-capacity upgrade nudge ── */}
      {atCapacity && (
        <div style={{
          marginBottom: 18, padding: "14px 18px", borderRadius: 12,
          background: T.amberBg, border: `1px solid ${T.amberBorder}`,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <Icon name="AlertTriangle" size={15} color={T.amber} strokeWidth={2} />
          <p style={{ fontSize: 12.5, color: "#92400e", margin: 0, flex: 1 }}>
            You have filled all {slotsTotal} Pro slots on your {plan.label} plan. Remove a Pro to free a slot, or upgrade your plan to reserve more.
          </p>
          {onNavigate && (
            <button
              onClick={() => onNavigate("billing")}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: T.amber, fontSize: 12.5, fontWeight: 700,
                fontFamily: T.sans, whiteSpace: "nowrap", padding: 0,
              }}>
              Upgrade plan
            </button>
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: T.n900, margin: 0 }}>Your reserved Pros</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            disabled={atCapacity}
            onClick={() => { if (!atCapacity) navigate("/search?mode=business-pro&context=reserve"); }}
            style={{
              padding: "10px 18px", borderRadius: 10, border: "none",
              background: atCapacity ? T.n200 : T.g500, color: T.white, fontSize: 13, fontWeight: 600,
              fontFamily: T.sans, cursor: atCapacity ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}
            onMouseEnter={e => { if (!atCapacity) e.currentTarget.style.background = T.g600; }}
            onMouseLeave={e => { if (!atCapacity) e.currentTarget.style.background = atCapacity ? T.n200 : T.g500; }}>
            <Icon name="UserPlus" size={15} color={T.white} strokeWidth={2} />
            Reserve a Pro
          </button>
          {!atCapacity && (
            <button
              onClick={() => setModalOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.n500, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, textDecoration: "underline" }}>
              or browse here
            </button>
          )}
        </div>
      </div>

      {reserved.length === 0 ? (
        <div style={{ padding: "48px 20px", textAlign: "center", borderRadius: 16, border: `1.5px dashed ${T.n200}` }}>
          <Icon name="Users" size={32} color={T.n200} strokeWidth={1.3} />
          <p style={{ fontSize: 13.5, color: T.n400, margin: "12px 0 0" }}>No Pros reserved yet. Build your team to get started.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {reserved.map(p => (
            <TeamProCard key={p.id} pro={p} onRemove={handleRemove} canManage={canManage} />
          ))}
        </div>
      )}

      <ReserveProModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        slotsRemaining={slotsRemaining}
        alreadyReserved={reserved}
        pool={pool}
        isPriorityPool={isPriorityPool}
      />
    </div>
  );
}